import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import {
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Link
} from '@material-ui/core';
import { ccToPcConverter } from 'helpers/commonUtils';
import Tooltip from '@material-ui/core/Tooltip';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

const useStyles = makeStyles((theme) => ({
  labelDiv: {
    display: 'flex',
    margin: '8px 32px'
  },
  innerKeyDiv: {
    width: '400px',
    fontWeight: 500,
    overflowWrap: 'anywhere'
  },
  innerValueDiv: {
    width: '400px',
    overflowWrap: 'anywhere'
  },
  dialogBox: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '12px'
  },
  closeButton: {
    color: theme.palette.warning.main
  },
  openTabIcon: {
    cursor: 'pointer',
    paddingLeft: '250px',
    paddingTop: '17px',
    '&:hover': {
      color: 'blue'
    }
  },
  dialogActions: {
    padding: '20px'
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    margin: '20%'
  },
  indentation: {
    margin: '8px 48px'
  },
  indentationFont: {
    width: '400px',
    fontWeight: 400,
    overflowWrap: 'anywhere'
  },
  zebraEven: {
    backgroundColor: theme.palette.background.paper
  },

  zebraOdd: {
    backgroundColor: theme.palette.action.hover
  },

  rowContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',

    '&:hover $copyIcon': {
      opacity: 1
    }
  },

  copyIcon: {
    opacity: 0,
    transition: 'opacity 0.2s ease',
    cursor: 'pointer',
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),

    '&:hover': {
      color: theme.palette.primary.main
    }
  }
}));

export default function DisplayModal(props) {
  const classes = useStyles();
  const {
    batch,
    isOpen,
    setIsOpen,
    title,
    isLoading,
    emptyMessage,
    size,
    isTable,
    isConversionRequired,
    isIndentationRequired,
    showNumberedInnerList,
    enableAlternateRowColors = false,
    hideParentKeys = false,
    keyColumnWidth = 400,
    innerListKeyColumnWidth = 40,
    enableCopyOnHover = false,
    copyTooltipText = 'Copy to Clipboard',
    customComponent,
    minWidth,
    minHeight,
    showOpenTabIcon,
    onOpenTabClick
  } = props;

  const [copiedValue, setCopiedValue] = React.useState('');

  const handleClose = () => {
    setIsOpen(false);
  };

  const displayKeyValuePairs = (
    obj,
    prop,
    isIndentationRequired,
    isArray,
    index
  ) => {
    return (
      <div
        className={`${classes.labelDiv} ${
          isIndentationRequired ? classes.indentation : ''
        } ${getRowClass(index)}`}
      >
        {!isTable && (
          <div
            className={
              isIndentationRequired
                ? classes.indentationFont
                : classes.innerKeyDiv
            }
            style={{
              width: isArray ? innerListKeyColumnWidth : keyColumnWidth,
              minWidth: isArray ? innerListKeyColumnWidth : keyColumnWidth,
              whiteSpace: 'nowrap'
            }}
          >
            {isConversionRequired
              ? ccToPcConverter(
                  `${
                    Array.isArray(obj) && isArray
                      ? showNumberedInnerList
                        ? +prop + 1
                        : ''
                      : prop
                  }`
                )
              : Array.isArray(obj) && isArray
              ? showNumberedInnerList
                ? +prop + 1
                : ''
              : prop}
          </div>
        )}
        <div className={classes.rowContainer}>
          <div className={classes.innerValueDiv}>
            {obj.hasOwnProperty(prop) ? (
              Array.isArray(obj[prop]) && obj[prop].length === 0 ? (
                '-'
              ) : typeof obj[prop] === 'object' ? (
                <Link
                  onClick={() => window.open(`${obj[prop].link}`, '_blank')}
                >
                  {obj[prop].label}
                </Link>
              ) : (
                obj[prop]
              )
            ) : (
              '-'
            )}
          </div>

          {enableCopyOnHover &&
            obj.hasOwnProperty(prop) &&
            typeof obj[prop] !== 'object' &&
            obj[prop] && (
              <Tooltip
                title={copiedValue === obj[prop] ? 'Copied' : copyTooltipText}
              >
                <FileCopyOutlinedIcon
                  fontSize="small"
                  className={classes.copyIcon}
                  onClick={() => copyToClipboard(obj[prop])}
                />
              </Tooltip>
            )}
        </div>
      </div>
    );
  };

  const getRowClass = (index) => {
    if (!enableAlternateRowColors) {
      return '';
    }

    return index % 2 === 0 ? classes.zebraEven : classes.zebraOdd;
  };

  const copyToClipboard = (value) => {
    navigator.clipboard.writeText(value);

    setCopiedValue(value);

    setTimeout(() => {
      setCopiedValue('');
    }, 2000);
  };

  return (
    <div>
      <Dialog
        maxWidth={size || 'sm'}
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={isOpen}
        PaperProps={{
          style: {
            minWidth: minWidth || 'auto',
            minHeight: minHeight || 'auto'
          }
        }}
      >
        <div className={classes.dialogBox}>
          <DialogTitle id="customized-dialog-title" onClose={handleClose}>
            <Typography variant="h2">{title}</Typography>
          </DialogTitle>
          {showOpenTabIcon && (
            <div
              onClick={() => onOpenTabClick()}
              className={classes.openTabIcon}
            >
              <Tooltip title="Open in new tab">
                <IconButton size="small">
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          )}
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <DialogContent>
          <Card>
            <CardContent>
              {isLoading ? (
                <div className={classes.loader}>
                  <CircularProgress />
                </div>
              ) : customComponent ? (
                customComponent
              ) : (
                <React.Fragment>
                  {emptyMessage && emptyMessage.show && (
                    <div style={{ padding: '20px' }}>{emptyMessage.msg}</div>
                  )}
                  {Object.keys(batch).map((prop) => {
                    return (
                      <div>
                        {batch.hasOwnProperty(prop) &&
                        typeof batch[prop] === 'object' &&
                        Object.keys(batch[prop])?.length > 0
                          ? !hideParentKeys && (
                              <div className={classes.labelDiv}>
                                <div className={classes.innerKeyDiv}>
                                  {isConversionRequired
                                    ? ccToPcConverter(`${prop}`)
                                    : prop}
                                </div>
                                <div className={classes.innerValueDiv}></div>
                              </div>
                            )
                          : displayKeyValuePairs(
                              batch,
                              prop,
                              false,
                              false,
                              Object.keys(batch).indexOf(prop)
                            )}
                        {batch.hasOwnProperty(prop) &&
                          typeof batch[prop] === 'object' &&
                          Object.keys(batch[prop])?.length > 0 &&
                          Object.keys(batch[prop]).map((item, index) => {
                            return displayKeyValuePairs(
                              batch[prop],
                              item,
                              isIndentationRequired,
                              true,
                              index
                            );
                          })}
                      </div>
                    );
                  })}
                </React.Fragment>
              )}
            </CardContent>
          </Card>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button variant="contained" onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
